import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';
import AIChatWidget from './components/AIChatWidget';
import '@fortawesome/fontawesome-free/css/all.min.css';

// Public Pages
import Home from './pages/Home';
import Packages from './pages/Packages';
import PackageDetails from './pages/PackageDetails';
import Booking from './pages/Booking';
import About from './pages/About';
import Contact from './pages/Contact';
import Destinations from './pages/Destinations';
import DestinationDetail from './pages/DestinationDetail';
import BookingSuccess from './pages/BookingSuccess';
import CampingBookingSuccess from './pages/CampingBookingSuccess';
import BookingTrack from './pages/BookingTrack';
import Invoice from './pages/Invoice';

// ✅ Camping Public Pages
import Camping from './pages/Camping';
import CampingDetail from './pages/CampingDetail';

import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Awards from './pages/Awards';
import BecomeHost from './pages/BecomeHost';
import MyBookings from './pages/MyBookings';
import Profile from './pages/Profile';


// Admin Pages
import AdminLogin from './pages/admin/Login';
import AdminDashboard from './pages/admin/Dashboard';
import AdminDestinations from './pages/admin/Destinations';
import AdminPackages from './pages/admin/Packages';
import AdminBookings from './pages/admin/Bookings';
import AdminMessages from './pages/admin/Messages';
import AdminNewsletter from './pages/admin/Newsletter';
import AdminUsers from './pages/admin/Users';
import AdminCoupons from './pages/admin/Coupons';
import AdminReviews from './pages/admin/Reviews';
import AdminAIChats from './pages/admin/AIChats';
import AdminAddonServices from './pages/admin/AddonServices';
import AdminHeroSlides from './pages/admin/HeroSlides';

// ✅ Admin Camping Pages
import AdminCamping from './pages/admin/camping/AdminCamping';
import AdminCampingLocations from './pages/admin/camping/AdminCampingLocations';
import AdminCampingSites from './pages/admin/camping/AdminCampingSites';
import AdminCampingBookings from './pages/admin/camping/AdminCampingBookings';
import AdminCampingStats from './pages/admin/camping/AdminCampingStats';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Wallet from './components/Wallet';
import AdminWallet from './pages/admin/AdminWallet';



import './styles/admin.css';
import './styles/main.css';

/* Protected Route */
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const user = safeParseUser(localStorage.getItem('user'));

  if (!token || !isAdminUser(user)) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

const safeParseUser = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const isAdminUser = (user) => {
  const role = String(user?.role || '').trim().toLowerCase();
  return role === 'admin' || role === 'super_admin' || role === 'superadmin';
};

function App() {
  useEffect(() => {
    console.log('🔍 Environment Variables Check:');
    console.log('REACT_APP_RAZORPAY_KEY_ID:', process.env.REACT_APP_RAZORPAY_KEY_ID);
    console.log('All REACT_APP vars:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* ========== PUBLIC ROUTES ========== */}
        <Route path="/" element={<><Header /><Home /><Footer /></>} />
        <Route path="/packages" element={<><Header /><Packages /><Footer /></>} />
        <Route path="/package/:id/:slug?" element={<><Header /><PackageDetails /><Footer /></>} />
        <Route path="/booking/:id" element={<><Header /><Booking /><Footer /></>} />
        <Route path="/about" element={<><Header /><About /><Footer /></>} />
        <Route path="/contact" element={<><Header /><Contact /><Footer /></>} />
        <Route path="/destinations" element={<><Header /><Destinations /><Footer /></>} />
        <Route path="/destinations/:id" element={<><Header /><DestinationDetail /><Footer /></>} />
        <Route path="/privacy" element={<><Header /><Privacy /><Footer /></>} />
        <Route path="/terms" element={<><Header /><Terms /><Footer /></>} />
        <Route path="/awards" element={<><Header /><Awards /><Footer /></>} />
        <Route path="/become-host" element={<><Header /><BecomeHost /><Footer /></>} />
        <Route path="/booking-success/:bookingId" element={<><Header /><BookingSuccess /><Footer /></>} />
        <Route path="/invoice/:bookingId" element={<Invoice />} />
        <Route path="/camping-booking-success/:bookingId" element={<><Header /><CampingBookingSuccess /><Footer /></>} />
        <Route path="/booking/track" element={<><Header /><BookingTrack /><Footer /></>} />
        <Route path="/booking/track/:bookingId" element={<><Header /><BookingTrack /><Footer /></>} />
        <Route path="/login" element={<><Header /><Login /><Footer /></>} />
        <Route path="/register" element={<><Header /><Register /><Footer /></>} />
        <Route path="/forgot-password" element={<><Header /><ForgotPassword /><Footer /></>} />
        <Route path="/reset-password/:token" element={<><Header /><ResetPassword /><Footer /></>} />
        <Route path="/my-bookings" element={<><Header /><MyBookings /><Footer /></>} />
        <Route path="/profile" element={<><Header /><Profile /><Footer /></>} />
        <Route path="/wallet" element={<><Header /><Wallet /><Footer /></>} />



        {/* ✅ CAMPING PUBLIC ROUTES */}
        <Route path="/camping" element={<><Header /><Camping /><Footer /></>} />
        <Route path="/camping/:slug" element={<><Header /><CampingDetail /><Footer /></>} />

        {/* ========== ADMIN ROUTES ========== */}
        <Route path="/admin/login" element={<AdminLogin />} />
        
        {/* Admin Dashboard */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Destinations */}
        <Route
          path="/admin/destinations"
          element={
            <ProtectedRoute>
              <AdminDestinations />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Packages */}
        <Route
          path="/admin/packages"
          element={
            <ProtectedRoute>
              <AdminPackages />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/hero-slides"
          element={
            <ProtectedRoute>
              <AdminHeroSlides />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Bookings */}
        <Route
          path="/admin/bookings"
          element={
            <ProtectedRoute>
              <AdminBookings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/coupons"
          element={
            <ProtectedRoute>
              <AdminCoupons />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/addon-services"
          element={
            <ProtectedRoute>
              <AdminAddonServices />
            </ProtectedRoute>
          }
        />
        
        {/* Admin Messages */}
        <Route
          path="/admin/messages"
          element={
            <ProtectedRoute>
              <AdminMessages />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/newsletter"
          element={
            <ProtectedRoute>
              <AdminNewsletter />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/reviews"
          element={
            <ProtectedRoute>
              <AdminReviews />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/ai-chats"
          element={
            <ProtectedRoute>
              <AdminAIChats />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminUsers />
            </ProtectedRoute>
          }
        />

        {/* ✅ ADMIN CAMPING ROUTES */}
        {/* Main Camping Management */}
        <Route
          path="/admin/camping"
          element={
            <ProtectedRoute>
              <AdminCamping />
            </ProtectedRoute>
          }
        />
        
        {/* Camping Stats (alias for main) */}
        <Route
          path="/admin/camping/stats"
          element={
            <ProtectedRoute>
              <AdminCampingStats />
            </ProtectedRoute>
          }
        />
        
        {/* Camping Locations */}
        <Route
          path="/admin/camping/locations"
          element={
            <ProtectedRoute>
              <AdminCampingLocations />
            </ProtectedRoute>
          }
        />
        
        {/* Edit specific location */}
        <Route
          path="/admin/camping/locations/:id"
          element={
            <ProtectedRoute>
              <AdminCampingLocations />
            </ProtectedRoute>
          }
        />
        
        {/* Camping Sites */}
        <Route
          path="/admin/camping/sites"
          element={
            <ProtectedRoute>
              <AdminCampingSites />
            </ProtectedRoute>
          }
        />
        
        {/* Edit specific site */}
        <Route
          path="/admin/camping/sites/:id"
          element={
            <ProtectedRoute>
              <AdminCampingSites />
            </ProtectedRoute>
          }
        />
        
        {/* Add new site */}
        <Route
          path="/admin/camping/sites/new"
          element={
            <ProtectedRoute>
              <AdminCampingSites />
            </ProtectedRoute>
          }
        />
        
        {/* Camping Bookings */}
        <Route
          path="/admin/camping/bookings"
          element={
            <ProtectedRoute>
              <AdminCampingBookings />
            </ProtectedRoute>
          }
        />
        
        {/* View specific booking */}
        <Route
          path="/admin/camping/bookings/:id"
          element={
            <ProtectedRoute>
              <AdminCampingBookings />
            </ProtectedRoute>
          }
        />

<Route
    path="/admin/wallet"
    element={
        <ProtectedRoute>
            <AdminWallet />
        </ProtectedRoute>
    }
/>
        

        {/* Fallback Route - 404 */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <AIChatWidget />
    </Router>
  );
}

export default App;
